import { z } from 'zod';
import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { FocusAreaDefinition } from 'libs/end-to-end/src/types/databases/focus-areas/focus-area-definition';
import { FilterSetDefinition } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterSetFilterDefinitions } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { CopilotDefinition } from 'libs/end-to-end/src/types/databases/copilots/copilot-definition';
import { UpsellDefinition } from 'libs/end-to-end/src/types/databases/copilots/upsell-definition';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { HiringNotificationEmailSubjectSuffixTemplate } from 'libs/end-to-end/src/types/hiring-notification-sender/hiring-notification-email-subject-suffix-template';
import { CopilotAutoMLConfiguration } from 'libs/end-to-end/src/copilot';
import { CommonRevisionFlags } from 'libs/end-to-end/src/common-revision-flags';

export const FullDatabaseRevisionSchema = CommonRevisionFlags.extend({
  dimensions: z.array(DimensionDefinition),

  /**
   * This is the default filter set, which is used for each of the tools (unless the tool has a custom filter set).
   */
  filters: FilterSetFilterDefinitions,

  /**
   * Here are all the custom filter sets that are defined to be usable with tools.
   */
  filterSets: z.array(FilterSetDefinition).optional(),
  focusAreas: z.array(FocusAreaDefinition),
  upsells: z.array(UpsellDefinition).optional(),
  calibrationFraming: CalibrationFraming.optional(),
  showAllInsights: z.boolean().optional(),
  showAIExplanations: z.boolean().optional(),
  showNewDataIntelligenceDeepDivePage: z.boolean().optional(),
  showSavedAnalysesPage: z.boolean().optional(),
  showDownloadMultiAnalysisButton: z.boolean().optional(),
  showDownloadSingleAnalysisButton: z.boolean().optional(),
  showDynamicPipelineAnalysisPage: z.boolean().optional(),
  showFobButtonInDashboardPage: z.boolean().optional(),
  showHeatmapAverages: z.boolean().optional(),
  sendHiringNotificationSummaryEmailAfterTopLevelAnalysis: z
    .boolean()
    .optional(),
  hiringNotificationEmailSubjectSuffixTemplate:
    HiringNotificationEmailSubjectSuffixTemplate.optional(),
  forceHiringNotificationSummaryToOnlySendToThisEmail: z
    .string()
    .email()
    .optional(),
  numPreviousDaysOfApplicantsForHiringNotificationSummaryEmail: z
    .number()
    .min(1)
    .optional(),
  minReadinessRatingForHiringNotificationSummaryEmail: z
    .number()
    .min(1)
    .optional(),
  additionalEmailsToSendAllHiringNotificationSummaryEmailsTo: z
    .string()
    .email()
    .array()
    .optional(),
  enableAtsWriteBack: z.boolean().optional(),
  hideFocusAreaSolutionsPage: z.boolean().optional(),
  hideEmployeeLifecyclePage: z.boolean().optional(),
  translationPhrasingCode: z.string().optional(),
  overrideUploadSnapshotDate: z.string().optional(),
  useDisparityOnRay: z.boolean().optional(),
  useDisparityOnParquet: z.boolean().optional(),
  useHeatmapOnRay: z.boolean().optional(),
  useFAOnRay: z.boolean().optional(),
  copilotAutoMLConfiguration: CopilotAutoMLConfiguration.optional(),
  enablePublicDashboards: z.boolean().optional(),
  showDashboardAISummaries: z.boolean().optional(),
  showChatbot: z.boolean().optional(),
}).describe('FullDatabaseRevisionSchema');

export type FullDatabaseRevisionSchema = z.infer<
  typeof FullDatabaseRevisionSchema
>;

// We exclude any fields here that are data set references, since they cause a cache miss on the dependency due to the schema changing.
export const DatabaseRevisionSchema = FullDatabaseRevisionSchema.omit({
  copilots: true,
})
  .extend({
    copilots: z.array(CopilotDefinition).optional(),
  })
  .describe('DatabaseRevisionSchema');

export type DatabaseRevisionSchema = z.infer<typeof DatabaseRevisionSchema>;

export const DatabaseRevisionSchemaWithoutFilters =
  FullDatabaseRevisionSchema.omit({
    filters: true,
  });

export type DatabaseRevisionSchemaWithoutFilters = z.infer<
  typeof DatabaseRevisionSchemaWithoutFilters
>;
